import LoadingBar from "@components/ui/LoadingBar";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformOrdresToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { isOwnerOrAdmin, useSimulateMemberView } from "@utils/permissions";
import { ordresApi } from "@services/api/ordres";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { searchOrdresFull } from "@services/algolia";
import OrdreStatusGuide from "@components/ui/OrdreStatusGuide";
import { useTagStore } from "@store/tagStore";
import { useSearchParams } from "react-router-dom";
import type { OrdreStatus, TrackingStep } from "types/ordre";
import ChatPanel from "@components/chat/ChatPanel";
import { useMessagerieStore } from "@store/messagerieStore";
import { messagerieApi } from "@services/api/messagerie";

const STATUS_TO_TRACKING: Partial<Record<OrdreStatus, TrackingStep>> = {
  a_preparer: "en_attente_depot",
  devis_initial: "devis_pret",
  commande: "en_attente_depot",
  pret: "en_attente_depot",
  reception: "vehicule_depose",
  en_cours: "reparations_en_cours",
  devis_supp: "devis_supplementaire",
  accord: "reparations_en_cours",
  acompte: "acompte_manquant",
  fin_de_travaux: "travaux_termines",
  facturation: "facture_disponible",
  solde: "solde_en_attente",
  solde_paye: "paiement_valide",
  disponible: "vehicule_disponible",
  restitue: "vehicule_restitue",
};

const statusTabs = [
  { value: "all", label: "Tous" },
  { value: "a_preparer", label: "A préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commande" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];

export default function OrdresScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  useSimulateMemberView();
  const canDelete = isOwnerOrAdmin(userData);
  const {
    ordres,
    isLoading,
    isRefreshing,
    hasMore,
    hasPrev,
    myOnly,
    totalCount,
    page,
    fetchOrdres,
    fetchNextPage,
    fetchPrevPage,
    goToPage,
    setSort,
    setMyOnly,
    removeOrdres,
  } = useOrdreStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const [table, setTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(() => {
    const param = searchParams.get("status");
    return param && statusTabs.some((t) => t.value === param) ? param : "all";
  });
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [flagBloque, setFlagBloque] = useState(() => searchParams.get("flag") === "bloque");
  const [flagUrgent, setFlagUrgent] = useState(() => searchParams.get("flag") === "urgent");
  const [chatOrdre, setChatOrdre] = useState<{ id: string; discussion_id?: string; internal_discussion_id?: string; name: string; phone?: string } | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterBtnRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleTagToggle = (tagId: string) => {
    setTagFilter((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  useEffect(() => {
    if (orgData?.id) {
      useTagStore.getState().fetchTags(orgData.id);
      const assignedTo = myOnly ? userData?.id : undefined;
      fetchOrdres(orgData.id, activeTab, assignedTo);
    }
  }, [orgData?.id, activeTab, myOnly]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!showFilterPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (filterBtnRef.current?.contains(target)) return;
      if (filterPanelRef.current?.contains(target)) return;
      setShowFilterPanel(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterPanel]);

  // Preload discussion into messagerie store when chat opens
  useEffect(() => {
    if (!chatOrdre?.discussion_id) return;
    const discId = chatOrdre.discussion_id;
    const already = useMessagerieStore.getState().discussions.find((d) => d.id === discId);
    if (already) return;
    messagerieApi.getDiscussion(discId).then((disc) => {
      useMessagerieStore.setState((s) => ({
        discussions: [...s.discussions, disc],
      }));
    }).catch(() => {});
  }, [chatOrdre?.discussion_id]);

  const activeFilterCount = (myOnly ? 1 : 0) + (flagBloque ? 1 : 0) + (flagUrgent ? 1 : 0) + tagFilter.length;
  const tags = useTagStore((s) => s.tags).filter((t) => t.scope === "ordre" && t.active).sort((a, b) => a.name.localeCompare(b.name));

  const filteredOrdres = useMemo(() => {
    let result = ordres;
    if (flagBloque) {
      result = result.filter((o) => o.flags && o.flags.includes("bloque"));
    }
    if (flagUrgent) {
      result = result.filter((o) => o.flags && o.flags.includes("urgent"));
    }
    if (tagFilter.length === 0) return result;
    return result.filter((o) =>
      (o.tag_ids || []).some((id) => tagFilter.includes(id))
    );
  }, [ordres, tagFilter, flagBloque, flagUrgent]);

  const handleTagsChange = async (ordreId: string, tagIds: string[]) => {
    try {
      const updated = await ordresApi.update(ordreId, { tag_ids: tagIds });
      useOrdreStore.getState().updateOrdre(updated);
    } catch {
      showNotification({ message: "Erreur lors de la mise à jour des tags", type: "error" });
    }
  };

  const handleStatusChange = async (ordreId: string, status: OrdreStatus) => {
    try {
      const updates: any = { status };
      const autoTracking = STATUS_TO_TRACKING[status];
      if (autoTracking) updates.tracking_step = autoTracking;
      const updated = await ordresApi.update(ordreId, updates);
      useOrdreStore.getState().updateOrdre(updated);
    } catch {
      showNotification({ message: "Erreur lors de la mise à jour du statut", type: "error" });
    }
  };

  useEffect(() => {
    const table = transformOrdresToTable(filteredOrdres, "all", handleTagsChange, handleStatusChange, setChatOrdre);
    setTable(table);
  }, [filteredOrdres]);

  const handleDeleteSelected = () => {
    removeOrdres(selectedIds);
    showNotification({ type: "success", message: `${selectedIds.length} ordre(s) supprimé(s)` });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  const handleMyOnlyToggle = (e: any) => {
    setMyOnly(e.target.checked);
  };


  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} ordre(s) ?`}
          message="Cette action est irréversible. Les ordres et toutes leurs données seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-500">
            <Text1>Ordres de réparation</Text1>
            <Text2>Retrouvez tous vos ordres de réparations ici.</Text2>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <OrdreStatusGuide />
            {canDelete && selectedIds.length > 0 && (
              <SecondaryButton
                startIcon={<Icon1 name="delete" size={18} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {`Supprimer la sélection (${selectedIds.length})`}
              </SecondaryButton>
            )}
          </div>
        </div>
      </div>

      {/* DataGrid area */}
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col gap-3 w-full">
        {(isLoading || isRefreshing) && <LoadingBar />}
        <div className="flex-1 min-h-0 w-full">
            {table && (
              <DataGrid
                storageKey="ordres"
                serverSideSorted
                selectable={canDelete}
                table={table}
                hide_lines={true}
                initial_saved_columns_showed={{ id: false, created_at: false }}
                tabs={statusTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onSearch={async (query) => {
                  if (!orgData?.id) return [];
                  return searchOrdresFull(query, orgData.id);
                }}
                onSearchSelect={(option) => navigateTo({ route: "/ordre/" + option.value })}
                onClick={(item) => navigateTo({ route: "/ordre/" + item.id })}
                handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                hasNext={hasMore}
                hasPrev={hasPrev}
                onNext={() => orgData?.id && fetchNextPage(orgData.id)}
                onPrev={() => orgData?.id && fetchPrevPage(orgData.id)}
                totalRows={totalCount}
                serverPage={page}
                totalServerPages={Math.ceil(totalCount / 25)}
                onServerPageChange={(p) => orgData?.id && goToPage(orgData.id, p)}
                handleSortChange={(model) => {
                  if (orgData?.id && model.length > 0)
                    setSort(orgData.id, model[0].field, model[0].sort);
                }}
                extraSearchContent={
                  <div ref={filterBtnRef}>
                    <SecondaryButton
                      startIcon={<Icon1 name="filters" size={16} color={activeFilterCount > 0 ? "#2563EB" : "#525252"} />}
                      onClick={() => {
                        if (filterBtnRef.current) {
                          const rect = filterBtnRef.current.getBoundingClientRect();
                          setFilterPos({ top: rect.bottom + 4, left: rect.left });
                        }
                        setShowFilterPanel((v) => !v);
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        Filtres
                        {activeFilterCount > 0 && (
                          <span className="bg-blue-500 text-white rounded-full text-h5 w-4 h-4 flex items-center justify-center">
                            {activeFilterCount}
                          </span>
                        )}
                      </span>
                    </SecondaryButton>
                  </div>
                }
              />
            )}
          </div>
      </div>

      {/* Floating chat panel */}
      {chatOrdre && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 400, height: 600, maxHeight: "calc(100vh - 48px)", background: "#FFFFFF", borderColor: "#D9D9D9" }}>
          <div className="flex flex-row items-center justify-between px-4 py-2 border-b border-neutral-200">
            <span className="text-h2 font-medium truncate">{chatOrdre.name || "Conversation"}</span>
            <button onClick={() => setChatOrdre(null)} className="hover:bg-neutral-100 rounded-full p-1 flex-shrink-0">
              <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
                <path d="M1 1L11 11M11 1L1 11" stroke="#A1A1A1" strokeWidth={1.5} strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChatPanel
              discussionId={chatOrdre.discussion_id}
              internalDiscussionId={chatOrdre.internal_discussion_id}
              entityType="ordre"
              entityId={chatOrdre.id}
              entityName={chatOrdre.name}
              members={[]}
              onlineCount={0}
              compact
              whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
            />
          </div>
        </div>
      )}

      {/* Filter panel (portal) */}
      {showFilterPanel && createPortal(
        <div
          ref={filterPanelRef}
          className="bg-white shadow-lg rounded-xl border flex flex-col"
          style={{ position: "fixed", top: filterPos.top, left: filterPos.left, zIndex: 9999, width: 280 }}
        >
          {/* Mes ordres */}
          <div className="px-3 py-2.5 border-b">
            <CheckBoxInput
              checked={myOnly}
              onChange={handleMyOnlyToggle}
              label="Mes ordres"
            />
          </div>
          {/* Drapeaux */}
          <div className="px-3 py-2.5 border-b flex flex-col gap-2">
            <div className="text-h4 text-neutral-400">Drapeaux</div>
            <div className="flex flex-row gap-2">
              <button
                type="button"
                onClick={() => setFlagBloque((v) => !v)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-h4 font-medium transition-colors"
                style={{
                  background: flagBloque ? "#DC2626" : "#FEE2E2",
                  color: flagBloque ? "#FFFFFF" : "#DC2626",
                }}
              >
                <Icon1 name="warning" size={12} color={flagBloque ? "#FFFFFF" : "#DC2626"} />
                Bloqué
              </button>
              <button
                type="button"
                onClick={() => setFlagUrgent((v) => !v)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-h4 font-medium transition-colors"
                style={{
                  background: flagUrgent ? "#D97706" : "#FEF3C7",
                  color: flagUrgent ? "#FFFFFF" : "#D97706",
                }}
              >
                <Icon1 name="flag" size={12} color={flagUrgent ? "#FFFFFF" : "#D97706"} />
                Urgent
              </button>
            </div>
          </div>
          {/* Tags */}
          {tags.length > 0 && (
            <div className="px-3 py-2.5 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-h4 text-neutral-400">Tags</span>
                {tagFilter.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTagFilter([])}
                    className="text-h5 text-neutral-400 hover:text-neutral-600"
                  >
                    Effacer
                  </button>
                )}
              </div>
              <div className="flex flex-row flex-wrap gap-1.5">
                {tags.map((tag) => {
                  const isSelected = tagFilter.includes(tag.id);
                  return (
                    <span
                      key={tag.id}
                      className="text-h4 whitespace-nowrap cursor-pointer select-none"
                      onClick={() => handleTagToggle(tag.id)}
                      style={{
                        background: isSelected ? tag.color : "transparent",
                        color: isSelected ? "#FFFFFF" : tag.color,
                        border: `1.5px solid ${tag.color}`,
                        borderRadius: 30,
                        padding: "2px 10px",
                        lineHeight: "16px",
                        opacity: isSelected ? 1 : 0.7,
                      }}
                    >
                      {tag.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
